'use client'

import { ArrowRight, Sparkles } from 'lucide-react'
import { useRouter } from 'next/navigation'
import type React from 'react'
import { useEffect, useRef, useState } from 'react'

const EXAMPLE_QUERIES = [
  'desenvolvedor remoto pagando 8k',
  'vaga de marmoreiro em Cachoeiro-ES',
  'estágio TI Vitória home office',
  'vendedor com vale-transporte',
  'motorista categoria D Serra',
]

const AI_STEPS = [
  { emoji: '🧠', label: 'Entendendo intenção' },
  { emoji: '🔍', label: 'Buscando em 50+ fontes' },
  { emoji: '⚡', label: 'Ranqueando por relevância' },
]

export function HeroAIDemo() {
  const router = useRouter()
  const [query, setQuery] = useState('')
  const [placeholder, setPlaceholder] = useState('')
  const [stepsVisible, setStepsVisible] = useState(false)
  const queryIndexRef = useRef(0)
  const charIndexRef = useRef(0)
  const directionRef = useRef<'typing' | 'pausing' | 'erasing'>('typing')
  const pauseCountRef = useRef(0)

  useEffect(() => {
    setStepsVisible(true)

    const interval = setInterval(() => {
      const currentQuery = EXAMPLE_QUERIES[queryIndexRef.current] ?? ''

      if (directionRef.current === 'typing') {
        charIndexRef.current += 1
        setPlaceholder(currentQuery.slice(0, charIndexRef.current))
        if (charIndexRef.current >= currentQuery.length) {
          directionRef.current = 'pausing'
          pauseCountRef.current = 0
        }
      } else if (directionRef.current === 'pausing') {
        pauseCountRef.current += 1
        if (pauseCountRef.current >= 40) {
          directionRef.current = 'erasing'
        }
      } else {
        charIndexRef.current -= 1
        setPlaceholder(currentQuery.slice(0, charIndexRef.current))
        if (charIndexRef.current <= 0) {
          queryIndexRef.current = (queryIndexRef.current + 1) % EXAMPLE_QUERIES.length
          charIndexRef.current = 0
          directionRef.current = 'typing'
        }
      }
    }, 50)

    return () => clearInterval(interval)
  }, [])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    const q = query.trim() || placeholder
    if (q) router.push(`/buscar?q=${encodeURIComponent(q)}`)
  }

  return (
    <div className="w-full max-w-2xl mx-auto px-4">
      {/* Tooltip */}
      <div className="flex justify-center mb-3">
        <span className="inline-flex items-center gap-1.5 rounded-full bg-white/60 backdrop-blur-sm border border-blue-100/80 px-3 py-1 text-[10px] font-semibold uppercase tracking-widest text-blue-600 shadow-sm">
          <Sparkles className="h-3 w-3" aria-hidden="true" />
          Inteligência Artificial em ação
        </span>
      </div>

      {/* Search box */}
      {/* biome-ignore lint/a11y/useSemanticElements: form é wrapper search semântico */}
      <form onSubmit={handleSubmit} className="relative" role="search">
        <div className="relative flex flex-col sm:flex-row sm:items-center rounded-2xl bg-white/80 backdrop-blur-md border border-blue-100 shadow-xl shadow-blue-500/10 focus-within:ring-2 focus-within:ring-blue-400/40 focus-within:border-blue-300 transition-all">
          <div className="relative flex-1 flex items-center">
            <Sparkles
              className="absolute left-4 h-5 w-5 text-blue-500 flex-shrink-0 pointer-events-none"
              aria-hidden="true"
            />
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={placeholder || 'Digite o que você busca…'}
              aria-label="Buscar vagas com IA"
              className="w-full bg-transparent pl-12 pr-4 py-4 text-base text-gray-800 placeholder:text-gray-400 outline-none rounded-2xl"
            />
          </div>
          <button
            type="submit"
            className="w-full sm:w-auto m-1.5 flex items-center justify-center gap-2 bg-gradient-to-r from-blue-600 to-violet-600 hover:from-blue-700 hover:to-violet-700 text-white font-semibold px-5 py-2.5 rounded-xl transition-all shadow-md shadow-blue-500/25 hover:shadow-blue-500/40 text-sm whitespace-nowrap group"
            aria-label="Buscar com IA"
          >
            Buscar com IA
            <ArrowRight
              className="h-4 w-4 group-hover:translate-x-0.5 transition-transform"
              aria-hidden="true"
            />
          </button>
        </div>
      </form>

      {/* AI thinking steps */}
      <div
        className="mt-5 flex flex-wrap justify-center gap-3 sm:gap-6"
        aria-label="Como a IA processa sua busca"
      >
        {AI_STEPS.map((step, i) => (
          <div
            key={step.label}
            className="flex items-center gap-2 transition-all duration-500"
            style={{
              opacity: stepsVisible ? 1 : 0,
              transform: stepsVisible ? 'translateY(0)' : 'translateY(8px)',
              transitionDelay: `${i * 120 + 200}ms`,
            }}
          >
            <span className="text-base" aria-hidden="true">
              {step.emoji}
            </span>
            <span className="text-xs font-medium text-gray-600">{step.label}</span>
          </div>
        ))}
      </div>
    </div>
  )
}
